import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../config/api';
import {
  PlusIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BuildingOffice2Icon,
  UsersIcon,
  UserGroupIcon,
  BookOpenIcon,
  ShieldCheckIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import demoData from '../data/demo_institutions.json';

interface InstitutionSummary {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  logo_url: string | null;
  is_active: boolean;
  group_count: number | null;
  member_count: number | null;
}

interface MemberDetail {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  joined_at: string | null;
  group_id: string | null;
}

interface GroupDetail {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  member_count: number;
  book_count: number;
  members: MemberDetail[];
}

interface InstitutionFullDetail {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  logo_url: string | null;
  cover_image_url: string | null;
  is_active: boolean;
  created_at: string | null;
  admins: MemberDetail[];
  sub_admins: MemberDetail[];
  groups: GroupDetail[];
}

const AdminInstitutions: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  const [institutions, setInstitutions] = useState<InstitutionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<InstitutionFullDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Assign admin form
  const [showAssignAdmin, setShowAssignAdmin] = useState<string | null>(null);
  const [assignAdminEmail, setAssignAdminEmail] = useState('');
  const [assigningAdmin, setAssigningAdmin] = useState(false);
  const [assignError, setAssignError] = useState('');

  // Convert demo JSON to InstitutionSummary format
  const demoInstitutions: InstitutionSummary[] = demoData.institutions.map((inst) => ({
    id: inst.id,
    name: inst.name,
    slug: inst.slug,
    description: inst.description,
    logo_url: inst.logo_url,
    is_active: inst.is_active,
    group_count: inst.group_count,
    member_count: inst.member_count,
  }));

  const fetchInstitutions = useCallback(async () => {
    try {
      const response = await api.get('/api/institutions');
      // Merge real API data with demo data
      setInstitutions([...response.data, ...demoInstitutions]);
    } catch (error) {
      console.error('Failed to fetch institutions:', error);
      // Even if API fails, show demo data
      setInstitutions(demoInstitutions);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInstitutions();
  }, [fetchInstitutions]);

  const isDemo = (instId: string) => instId.startsWith('demo-');

  const fetchDetail = async (instId: string) => {
    setDetailLoading(true);

    // For demo institutions, build detail from JSON
    if (isDemo(instId)) {
      const demoInst = demoData.institutions.find((i) => i.id === instId);
      if (demoInst) {
        setDetail({
          id: demoInst.id,
          name: demoInst.name,
          slug: demoInst.slug,
          description: demoInst.description,
          logo_url: demoInst.logo_url,
          cover_image_url: demoInst.cover_image_url,
          is_active: demoInst.is_active,
          created_at: demoInst.created_at,
          admins: [{ id: 'demo-admin', user_id: 'demo-admin', email: demoInst.admin.email, full_name: demoInst.admin.name, role: 'institution_admin', joined_at: demoInst.created_at, group_id: null }],
          sub_admins: demoInst.sub_admins.map((sa, i) => ({ id: `demo-sa-${i}`, user_id: `demo-sa-${i}`, email: sa.email, full_name: sa.name, role: 'institution_sub_admin', joined_at: demoInst.created_at, group_id: null })),
          groups: demoInst.groups.map((g) => ({
            id: g.id,
            name: g.name,
            description: g.description,
            is_active: g.is_active,
            member_count: g.student_count,
            book_count: g.book_count,
            members: g.students.map((s, i) => ({ id: `demo-m-${i}`, user_id: `demo-m-${i}`, email: s.email, full_name: s.name, role: 'student', joined_at: demoInst.created_at, group_id: g.id })),
          })),
        });
      }
      setDetailLoading(false);
      return;
    }

    try {
      const response = await api.get(`/api/institutions/${instId}/full-detail`);
      setDetail(response.data);
    } catch (error) {
      console.error('Failed to fetch detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleToggleExpand = (instId: string) => {
    if (expandedId === instId) {
      setExpandedId(null);
      setDetail(null);
    } else {
      setExpandedId(instId);
      fetchDetail(instId);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim() || !newSlug.trim()) return;
    setCreating(true);
    setCreateError('');
    try {
      await api.post('/api/institutions', {
        name: newName.trim(),
        slug: newSlug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-'),
        description: newDesc.trim() || null,
        admin_email: newAdminEmail.trim() || null,
      });
      setNewName('');
      setNewSlug('');
      setNewDesc('');
      setNewAdminEmail('');
      setShowCreate(false);
      fetchInstitutions();
    } catch (error: any) {
      setCreateError(error.response?.data?.detail || 'Failed to create institution');
    } finally {
      setCreating(false);
    }
  };

  const handleAssignAdmin = async (instId: string) => {
    if (!assignAdminEmail.trim()) return;
    setAssigningAdmin(true);
    setAssignError('');
    try {
      await api.post(`/api/institutions/${instId}/institution-admins`, {
        email: assignAdminEmail.trim(),
      });
      setAssignAdminEmail('');
      setShowAssignAdmin(null);
      fetchDetail(instId);
    } catch (error: any) {
      setAssignError(error.response?.data?.detail || 'Failed to assign admin');
    } finally {
      setAssigningAdmin(false);
    }
  };

  const handleRemoveAdmin = async (instId: string, userId: string) => {
    if (!window.confirm('Remove this institution admin?')) return;
    try {
      await api.delete(`/api/institutions/${instId}/institution-admins/${userId}`);
      fetchDetail(instId);
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to remove admin');
    }
  };

  const cardClass = `rounded-xl border ${isDarkMode ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200'}`;
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className={`text-2xl font-bold ${textPrimary}`}>Manage Institutions</h1>
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5"
            style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}
          >
            <PlusIcon className="h-4 w-4" />
            Create Institution
          </button>
        </div>

        {/* Stats */}
        <div className={`grid grid-cols-3 gap-4 mb-6`}>
          <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200'}`}>
            <div className={`text-2xl font-bold ${textPrimary}`}>{institutions.length}</div>
            <div className={`text-sm ${textSecondary}`}>Total Institutions</div>
          </div>
          <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200'}`}>
            <div className={`text-2xl font-bold ${textPrimary}`}>
              {institutions.reduce((sum, i) => sum + (i.group_count ?? 0), 0)}
            </div>
            <div className={`text-sm ${textSecondary}`}>Total Groups</div>
          </div>
          <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-gray-200'}`}>
            <div className={`text-2xl font-bold ${textPrimary}`}>
              {institutions.reduce((sum, i) => sum + (i.member_count ?? 0), 0)}
            </div>
            <div className={`text-sm ${textSecondary}`}>Total Members</div>
          </div>
        </div>

        {/* Institution List */}
        <div className="space-y-4">
          {institutions.map((inst) => (
            <div key={inst.id} className={cardClass}>
              {/* Summary Row */}
              <button
                onClick={() => handleToggleExpand(inst.id)}
                className="w-full flex items-center justify-between p-4 text-left"
              >
                <div className="flex items-center gap-4">
                  {inst.logo_url ? (
                    <img src={inst.logo_url} alt={inst.name} className="h-10 w-10 rounded-lg object-cover" />
                  ) : (
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                      <BuildingOffice2Icon className="h-5 w-5 text-cyan-500" />
                    </div>
                  )}
                  <div>
                    <div className={`font-semibold ${textPrimary}`}>{inst.name}</div>
                    <div className={`text-sm ${textSecondary}`}>{inst.slug}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className={`flex gap-4 text-sm ${textSecondary}`}>
                    <span>{inst.group_count ?? 0} groups</span>
                    <span>{inst.member_count ?? 0} members</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/institution/${inst.id}`);
                    }}
                    className="text-xs text-cyan-500 hover:text-cyan-400 mr-2"
                  >
                    Manage
                  </button>
                  {expandedId === inst.id ? (
                    <ChevronUpIcon className={`h-5 w-5 ${textSecondary}`} />
                  ) : (
                    <ChevronDownIcon className={`h-5 w-5 ${textSecondary}`} />
                  )}
                </div>
              </button>

              {/* Expanded Detail */}
              {expandedId === inst.id && (
                <div className={`border-t px-4 pb-4 ${isDarkMode ? 'border-slate-700/50' : 'border-gray-200'}`}>
                  {detailLoading ? (
                    <div className="flex justify-center py-6">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
                    </div>
                  ) : detail ? (
                    <div className="space-y-4 pt-4">
                      {/* Admins */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className={`text-sm font-semibold flex items-center gap-1.5 ${textPrimary}`}>
                            <ShieldCheckIcon className="h-4 w-4 text-cyan-500" />
                            Institution Admins
                          </h4>
                          {!isDemo(inst.id) && (
                            <button
                              onClick={() => {
                                setShowAssignAdmin(inst.id);
                                setAssignAdminEmail('');
                                setAssignError('');
                              }}
                              className="text-xs text-cyan-500 hover:text-cyan-400 flex items-center gap-1"
                            >
                              <PlusIcon className="h-3 w-3" /> Add Admin
                            </button>
                          )}
                        </div>

                        {showAssignAdmin === inst.id && (
                          <div className="flex gap-2 mb-3">
                            <input
                              type="email"
                              placeholder="Email address"
                              value={assignAdminEmail}
                              onChange={(e) => setAssignAdminEmail(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleAssignAdmin(inst.id)}
                              className={`flex-1 px-3 py-1.5 rounded-lg border text-sm ${
                                isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                              }`}
                            />
                            <button
                              onClick={() => handleAssignAdmin(inst.id)}
                              disabled={assigningAdmin}
                              className="px-3 py-1.5 rounded-lg text-white text-sm disabled:opacity-50"
                              style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}
                            >
                              {assigningAdmin ? '...' : 'Add'}
                            </button>
                            <button
                              onClick={() => setShowAssignAdmin(null)}
                              className={`px-2 py-1.5 rounded-lg text-sm ${textSecondary}`}
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                        {assignError && <p className="text-red-400 text-xs mb-2">{assignError}</p>}

                        {detail.admins.length === 0 ? (
                          <p className={`text-sm ${textSecondary}`}>No admins assigned.</p>
                        ) : (
                          <div className="space-y-1">
                            {detail.admins.map((a) => (
                              <div key={a.id} className={`flex items-center justify-between py-1.5 px-3 rounded-lg text-sm ${
                                isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'
                              }`}>
                                <span className={textPrimary}>
                                  {a.full_name || a.email}
                                  {a.full_name && <span className={`ml-2 ${textSecondary}`}>{a.email}</span>}
                                </span>
                                {!isDemo(inst.id) && (
                                  <button
                                    onClick={() => handleRemoveAdmin(inst.id, a.user_id)}
                                    className="text-red-400 hover:text-red-300"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Sub-Admins */}
                      {detail.sub_admins.length > 0 && (
                        <div>
                          <h4 className={`text-sm font-semibold mb-2 ${textPrimary}`}>Sub-Admins</h4>
                          <div className="space-y-1">
                            {detail.sub_admins.map((sa) => (
                              <div key={sa.id} className={`py-1.5 px-3 rounded-lg text-sm ${
                                isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'
                              }`}>
                                <span className={textPrimary}>{sa.full_name || sa.email}</span>
                                {sa.full_name && <span className={`ml-2 ${textSecondary}`}>{sa.email}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Groups */}
                      <div>
                        <h4 className={`text-sm font-semibold mb-2 flex items-center gap-1.5 ${textPrimary}`}>
                          <UserGroupIcon className="h-4 w-4 text-cyan-500" />
                          Groups ({detail.groups.length})
                        </h4>
                        {detail.groups.length === 0 ? (
                          <p className={`text-sm ${textSecondary}`}>No groups yet.</p>
                        ) : (
                          <div className="space-y-2">
                            {detail.groups.map((group) => (
                              <div key={group.id} className={`p-3 rounded-lg border ${
                                isDarkMode ? 'border-slate-700/50 bg-slate-800/30' : 'border-gray-100 bg-gray-50'
                              } ${!group.is_active ? 'opacity-50' : ''}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className={`font-medium text-sm ${textPrimary}`}>{group.name}</span>
                                    {!group.is_active && (
                                      <span className="text-xs px-1.5 py-0.5 rounded bg-red-500/10 text-red-400">Inactive</span>
                                    )}
                                  </div>
                                  <div className={`flex gap-3 text-xs ${textSecondary}`}>
                                    <span className="flex items-center gap-1">
                                      <UsersIcon className="h-3 w-3" /> {group.member_count}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <BookOpenIcon className="h-3 w-3" /> {group.book_count}
                                    </span>
                                  </div>
                                </div>
                                {group.members.length > 0 && (
                                  <div className={`text-xs ${textSecondary} space-y-0.5`}>
                                    {group.members.slice(0, 5).map((m) => (
                                      <div key={m.id}>
                                        {m.full_name || m.email}
                                        <span className={`ml-1.5 px-1.5 py-0.5 rounded ${
                                          m.role === 'teacher' ? 'bg-blue-500/10 text-blue-400' : 'bg-green-500/10 text-green-400'
                                        }`}>{m.role}</span>
                                      </div>
                                    ))}
                                    {group.members.length > 5 && (
                                      <div className="text-cyan-500">+{group.members.length - 5} more</div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>

        {institutions.length === 0 && (
          <div className={`text-center py-12 ${textSecondary}`}>
            <BuildingOffice2Icon className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No institutions yet. Create your first one.</p>
          </div>
        )}

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className={`w-full max-w-md mx-4 p-6 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${textPrimary}`}>Create Institution</h3>
                <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-300">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Institution name"
                  value={newName}
                  onChange={(e) => {
                    setNewName(e.target.value);
                    setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''));
                  }}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
                <input
                  type="text"
                  placeholder="Slug (URL-safe identifier)"
                  value={newSlug}
                  onChange={(e) => setNewSlug(e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg border text-sm ${
                    isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
                <textarea
                  placeholder="Description (optional)"
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  rows={2}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
                <input
                  type="email"
                  placeholder="Institution admin email (optional)"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              {createError && <p className="text-red-400 text-sm mt-2">{createError}</p>}

              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => setShowCreate(false)}
                  className={`px-4 py-2 rounded-lg text-sm ${isDarkMode ? 'text-gray-400 hover:bg-slate-700' : 'text-gray-500 hover:bg-gray-100'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={creating || !newName.trim() || !newSlug.trim()}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}
                >
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminInstitutions;
